import { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  applyActionCode,
  checkActionCode,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { FIREBASE_AUTH, FIRESTORE_DB } from '../firebaseConfig';
import PropTypes from 'prop-types';
import {
  identify as phIdentify,
  alias as phAlias,
  reset as phReset,
  capture as phCapture,
  deriveSignupSource,
} from '../services/posthog';

// Helper function to translate Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
    case 'auth/missing-password':
      return 'Please enter a password.';
    case 'auth/missing-email':
      return 'Please enter an email address.';
    case 'auth/invalid-password':
      return 'Invalid password. Please try again.';
    case 'auth/requires-recent-login':
      return 'Please sign out and sign in again to perform this action.';
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used.';
    case 'auth/user-token-expired':
      return 'Your session has expired. Please sign in again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

// Fire the Google Ads "Sign-up" conversion (gtag.js is loaded in index.html).
// Only call this on actual account creation, not on every login.
const trackSignupConversion = () => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: 'AW-17177908199/kArrCJzcyfMbEOeniP8_',
    });
  }
};

const AuthContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const [userDoc, tokenResult] = await Promise.all([
            getDoc(doc(FIRESTORE_DB, 'users', firebaseUser.uid)),
            firebaseUser.getIdTokenResult(),
          ]);
          const userData = userDoc.exists() ? userDoc.data() : {};
          const isAdmin = tokenResult.claims.admin === true;

          // Auto-fix: if profileSet but not profileComplete, set it
          if (userData.profileSet && !userData.profileComplete) {
            updateDoc(doc(FIRESTORE_DB, 'users', firebaseUser.uid), { profileComplete: true }).catch(() => {});
            userData.profileComplete = true;
          }

          // Mark the user as recently active so they appear in "online now"
          // discovery (mirrors the mobile app's lastAppOpen write on app open).
          updateDoc(doc(FIRESTORE_DB, 'users', firebaseUser.uid), { lastAppOpen: new Date() }).catch(() => {});

          // Identify to PostHog (idempotent). Signup paths call alias(uid)
          // BEFORE this point so anonymous pre-signup activity stitches first;
          // for returning users / logins this is the only identify call.
          phIdentify(firebaseUser.uid);

          setUser({
            ...userData,
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || userData.displayName,
            isAdmin,
          });
        } catch (err) {
          console.error('Error fetching user data:', err);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Presence heartbeat: keep lastAppOpen fresh while the tab is open so web
  // users stay inside the "online now" window (30 min) rather than dropping off
  // after their initial page-load write. Re-writes on an interval and whenever
  // the tab regains visibility; skips writes while the tab is hidden so a
  // backgrounded tab doesn't burn writes (it expires from "online" naturally).
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return;

    const HEARTBEAT_MS = 5 * 60 * 1000; // 5 min; well within the 30-min window
    const touch = () => {
      updateDoc(doc(FIRESTORE_DB, 'users', uid), { lastAppOpen: new Date() }).catch(() => {});
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') touch();
    }, HEARTBEAT_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') touch();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user?.uid]);

  const signup = async (email, password, displayName) => {
    try {
      setLoading(true);
      
      const { user: firebaseUser } = await createUserWithEmailAndPassword(FIREBASE_AUTH, email, password);

      // Stitch anonymous pre-signup activity to the new uid, then identify.
      // Done synchronously right after account creation so it lands before the
      // onAuthStateChanged listener's identify (gated behind a Firestore read).
      // $set_once carries the immutable cohort-time signup props — set here
      // (genuine account creation) so a later app sign-in can't claim them.
      phAlias(firebaseUser.uid);
      phIdentify(firebaseUser.uid, undefined, { signup_platform: 'web', signup_method: 'email' });

      if (displayName) {
        await updateProfile(firebaseUser, { displayName });
      }
      
      await setDoc(doc(FIRESTORE_DB, 'users', firebaseUser.uid), {
        email: firebaseUser.email,
        displayName: displayName || '',
        firstName: displayName || '',
        createdAt: new Date(),
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      await setDoc(doc(FIRESTORE_DB, 'users', firebaseUser.uid, 'meta', 'firstLoad'), {
        created: new Date(),
        platform: 'web'
      });

      trackSignupConversion();

      // signup_completed (web mirror; the server user-doc trigger is canonical).
      // signup_source/invite_token_hash come from the current URL — a
      // ?continueUrl=/i/:token invite redirect makes this a room-invite signup.
      const source = await deriveSignupSource();
      phCapture('signup_completed', { method: 'email', platform: 'web', ...source });

      setError(null); // Only clear error on success
      return firebaseUser;
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
      setError(null); // Only clear error on success
      return result.user;
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(FIREBASE_AUTH, provider);
      const additionalUserInfo = getAdditionalUserInfo(result);
      const user = result.user;
      
      // Create/update user document in Firestore
      const userDocRef = doc(FIRESTORE_DB, 'users', user.uid);
      
      if (additionalUserInfo?.isNewUser) {
        // New user — stitch anonymous activity, then identify, before the
        // Firestore writes below let onAuthStateChanged's identify fire.
        // $set_once carries the immutable cohort-time signup props.
        phAlias(user.uid);
        phIdentify(user.uid, undefined, { signup_platform: 'web', signup_method: 'google' });

        // New user - create full profile
        await setDoc(userDocRef, {
          email: user.email,
          uid: user.uid,
          displayName: user.displayName || '',
          firstName: user.displayName?.split(' ')[0] || '',
          createdAt: new Date(),
          language: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          photoURL: user.photoURL
        });

        await setDoc(doc(FIRESTORE_DB, 'users', user.uid, 'meta', 'firstLoad'), {
          created: new Date(),
          platform: 'web'
        });

        trackSignupConversion();

        // signup_completed (web mirror) — only on a genuine new-user signup;
        // returning Google sign-INs are identified via onAuthStateChanged.
        const source = await deriveSignupSource();
        phCapture('signup_completed', { method: 'google', platform: 'web', ...source });
      } else {
        // Existing user - update basic info
        await setDoc(userDocRef, {
          email: user.email,
          uid: user.uid,
          photoURL: user.photoURL
        }, { merge: true });
      }

      setError(null);
      return user;
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(FIREBASE_AUTH);
      // Clear the PostHog distinct_id so the next (possibly different) user on
      // this browser isn't merged into the signed-out identity.
      phReset();
      setError(null); // Only clear error on success
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(FIREBASE_AUTH, email);
      setError(null); // Only clear error on success
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    }
  };

  // PPL-36 — branded action URL for the verification email.
  // Replaces Firebase's default ppldb-5cdba.firebaseapp.com/__/auth/action
  // landing with a page on the pplkpr.ai domain. `continueUrl` lets callers
  // (e.g. the mediation invite flow) bounce the user back to an in-app
  // destination after verification succeeds.
  // NOTE: pplkpr.ai is in Firebase Console → Authentication → Settings →
  //       Authorized domains for this URL to be accepted.
  const buildVerificationActionCodeSettings = (continueUrl) => {
    const base = `${window.location.origin}/auth/action`;
    const url = continueUrl
      ? `${base}?continueUrl=${encodeURIComponent(continueUrl)}`
      : base;
    return {
      url,
      handleCodeInApp: false,
    };
  };

  // PPL-36 — send (or re-send) the email-verification message for the
  // currently signed-in user. Callers may pass `continueUrl` to redirect
  // back into the app once the address is confirmed.
  const sendVerification = async (continueUrl) => {
    try {
      const current = FIREBASE_AUTH.currentUser;
      if (!current) {
        const msg = 'Please sign in before requesting a verification email.';
        setError(msg);
        throw new Error(msg);
      }
      await sendEmailVerification(current, buildVerificationActionCodeSettings(continueUrl));
      setError(null);
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    }
  };

  // PPL-36 — handle the `?mode=verifyEmail&oobCode=...` callback from the
  // verification email. Applies the action code, then reloads the current
  // Firebase user so downstream consumers see `emailVerified === true`.
  const applyVerification = async (oobCode) => {
    try {
      if (!oobCode) throw new Error('Missing oobCode.');
      // checkActionCode surfaces clearer errors than applyActionCode alone
      // (e.g. expired vs. invalid) — we let either path throw for the UI to
      // display via getFirebaseErrorMessage.
      await checkActionCode(FIREBASE_AUTH, oobCode);
      await applyActionCode(FIREBASE_AUTH, oobCode);
      try {
        await FIREBASE_AUTH.currentUser?.reload();
      } catch (reloadErr) {
        // Non-fatal — the verification itself succeeded. User may simply not
        // be signed in on this device/tab.
        console.warn('currentUser.reload after applyActionCode failed:', reloadErr);
      }
      setError(null);
    } catch (err) {
      const friendlyMessage = getFirebaseErrorMessage(err.code);
      setError(friendlyMessage);
      throw err;
    }
  };

  const value = {
    user,
    login,
    signup,
    signInWithGoogle,
    logout,
    resetPassword,
    sendVerification,
    applyVerification,
    loading,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Add PropTypes for component props validation
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};